/**
 * Admin preview of a What's New card — every kind, not just custom experiences.
 *
 * Default card: rendered by the SAME shared WhatsNewCard the student sees, fed
 * straight from the form, so what the admin previews is what ships (including
 * whether a heading appears at all and how the image fills its band).
 *
 * Custom experience: the tip's experienceKey selects a renderer from
 * EXPERIENCE_PREVIEWS; the admin picks a user (UserSearchFilter) and the
 * renderer fetches THAT user's data and renders the same shared presentational
 * component the student sees.
 *
 * Extensible: add a new experience by adding one entry to EXPERIENCE_PREVIEWS —
 * keep it in sync with the client registrations (see WhatsNewTips EXPERIENCE_KEYS).
 * See docs/FeatureDesigns/whats-new-chapter-progress.md.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { AssignmentReportView, ChapterProgressView, QuickPollView, toChapterStats, toPollConfig } from '@shared-ui/whats-new-experiences';
import { WhatsNewCard, type ImageFit, type WhatsNewPresentation } from '@shared-ui/whats-new';
import type {
  QuestionSessionReportState,
  QuestionSessionReportHeadline,
} from '@shared-ui/question-session-report';
import { userApi } from '../../api/userApi';
import './TipPreviewModal.css';

interface PreviewProps {
  userId: string;
  experienceData: Record<string, unknown>;
}

/** Chapter-progress preview: fetch the chosen user's tree, render the shared view. */
function ChapterProgressPreview({ userId, experienceData }: PreviewProps) {
  const subject = typeof experienceData.subject === 'string' ? experienceData.subject : 'chemistry';
  const subjectLabel = subject.charAt(0).toUpperCase() + subject.slice(1);
  const { data, status } = useQuery({
    queryKey: ['user-practice-tree', userId, subject],
    queryFn: () => userApi.getUserPracticeTree(userId, subject),
  });
  const viewStatus = status === 'pending' ? 'loading' : status === 'error' ? 'error' : 'ready';
  return (
    <ChapterProgressView
      subjectLabel={subjectLabel}
      status={viewStatus}
      chapters={toChapterStats(data?.nodes ?? [])}
      onPractice={() => { /* preview: CTA is inert (no navigation off the console) */ }}
    />
  );
}

interface AssignmentReportPayload {
  sessionId?: string;
  title?: string;
  submittedAt?: string;
  isAssignment?: boolean;
  report?: QuestionSessionReportState;
  headline?: QuestionSessionReportHeadline;
  hasMarking?: boolean;
}

/** Assignment-report preview: ask the server for the payload its serve-time gate
 *  would build for this user, and render it. A null payload is the informative
 *  case — that user has no report, so the gate would drop the tip for them. */
function AssignmentReportPreview({ userId, experienceData }: PreviewProps) {
  const maxAgeDays = typeof experienceData.maxAgeDays === 'number' ? experienceData.maxAgeDays : undefined;
  const { data, status } = useQuery({
    queryKey: ['user-latest-session-report', userId, maxAgeDays ?? null],
    queryFn: () => userApi.getUserLatestSessionReport(userId, maxAgeDays),
  });
  const payload = (data?.payload ?? {}) as AssignmentReportPayload;

  if (status === 'success' && !data?.payload) {
    return <p className="epm-note">This user has no ready assignment report — the tip would not be shown to them.</p>;
  }
  return (
    <AssignmentReportView
      status={status === 'pending' ? 'loading' : status === 'error' ? 'error' : 'ready'}
      title={payload.title}
      submittedAt={payload.submittedAt}
      isAssignment={payload.isAssignment}
      report={payload.report}
      headline={payload.headline}
      hasMarking={payload.hasMarking}
      onOpenReport={() => { /* preview: CTA is inert (no navigation off the console) */ }}
    />
  );
}

/** Quick-poll preview: the poll is driven entirely by experienceData, so there
 *  is no per-user data to fetch — render the shared view straight from the form. */
function QuickPollPreview({ experienceData }: PreviewProps) {
  const { question, options } = toPollConfig(experienceData);
  return (
    <QuickPollView
      question={question}
      options={options}
      onPick={() => { /* preview: recording an answer would be a real vote */ }}
      onLater={() => { /* preview: no snooze to apply */ }}
    />
  );
}

/** experienceKey → preview renderer. Add future experiences here. */
const EXPERIENCE_PREVIEWS: Record<string, (p: PreviewProps) => ReactNode> = {
  'chapter-progress': (p) => <ChapterProgressPreview {...p} />,
  'assignment-report': (p) => <AssignmentReportPreview {...p} />,
  'quick-poll': (p) => <QuickPollPreview {...p} />,
};

/** Experiences whose preview needs no user data — skip the picker for these. */
const USERLESS_PREVIEWS = new Set(['quick-poll']);

/** Does this experience key have an admin preview? (Drives the Preview button.) */
export function hasPreview(experienceKey: string): boolean {
  return !!EXPERIENCE_PREVIEWS[experienceKey];
}

/** Default-card content, straight off the form. */
export interface TipCardPreview {
  title?: string;
  body?: string;
  imageUrl?: string;
  imageFit?: ImageFit;
  /** 'close-only' drops the footer, leaving the ✕ as the card's one control. */
  presentation?: WhatsNewPresentation;
}

interface Props {
  /** '' = the default card, which previews from `card` and needs no user. */
  experienceKey: string;
  experienceData: Record<string, unknown>;
  card?: TipCardPreview;
  onClose: () => void;
  /** Preselect a user (e.g. opening the preview from a specific audience row) —
   *  the picker is still shown so the admin can switch. */
  initialUser?: UserSearchOption | null;
}

export function TipPreviewModal({ experienceKey, experienceData, card, onClose, initialUser }: Props) {
  // Keep the whole picked option, not just the email. The picker already knows
  // the userId; re-deriving it from the email meant a second fuzzy search whose
  // first hit could be a different person — or nobody at all.
  const [user, setUser] = useState<UserSearchOption | null>(initialUser ?? null);
  const renderer = EXPERIENCE_PREVIEWS[experienceKey];

  // Default card: no user data to resolve, so skip the picker and the console's
  // own modal chrome — render the real dialog exactly as it ships.
  if (!experienceKey) {
    return (
      <WhatsNewCard
        isOpen
        onClose={onClose}
        title={card?.title}
        body={card?.body}
        imageUrl={card?.imageUrl}
        imageFit={card?.imageFit}
        // 'close-only' has no footer at all — the ✕ this shell already draws is
        // the whole control set, which is exactly what the student would see.
        footer={card?.presentation === 'close-only' ? undefined : (
          <div className="wn-footer">
            <div className="wn-action-group">
              {/* Inert on purpose — the preview shows the controls' placement,
                  it does not snooze anything. */}
              <button className="wn-nav-btn" disabled>Dismiss</button>
              <button className="wn-nav-btn wn-nav-remind" disabled>Remind me later</button>
            </div>
          </div>
        )}
      />
    );
  }

  // Opened from a specific audience row → the user is fixed: drop the picker and
  // name them in the title. Opened from the tip form (no initialUser) → keep the
  // picker so the admin can choose whom to preview as.
  const userless = USERLESS_PREVIEWS.has(experienceKey);
  const fixedUser = !!initialUser;
  const userName = user?.name || user?.email;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content epm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{fixedUser && userName ? `Preview for ${userName}: ${experienceKey}` : `Preview: ${experienceKey}`}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {!fixedUser && !userless && (
            /* NOT a <label>: WebKit forwards a click on any descendant of a label
               to the label's control, including a nested <button>, where Chrome
               honours the interactive-content exception. Wrapping this composite
               widget in a label sent every result click to the search input in
               Safari, so picking a user silently did nothing. */
            <div className="epm-field">
              <span className="epm-field-label" id="epm-user-label">Show as user</span>
              <ScopedUserSearchFilter
                value={user?.email ?? ''}
                onChange={() => { /* onPick carries the selection; email alone is not enough */ }}
                onPick={setUser}
                valueText={user?.name}
                placeholder="Search by name or email…"
                excludeGuests
              />
            </div>
          )}

          {!renderer && <p className="epm-note">No preview registered for “{experienceKey}”.</p>}

          {renderer && (
            <div className="epm-stage">
              {userless || user
                ? <div className="epm-shell">{renderer({ userId: user?.userId ?? '', experienceData })}</div>
                : <p className="epm-note">Pick a user to preview this experience with their data.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
